// Vincular dispositivo (funnel v2, CC-CONSOLE#1523): lógica PURA de la
// pantalla /dispositivos/vincular. Sin React ni red, para poder probarla sola.
//
// Tres pasos: elegir el camino (equipo de WAYAK con código de caja, o GPS
// propio) → capturar los datos → prueba de compatibilidad (la primera
// posición). El backend decide la compatibilidad y el estado; aquí se valida
// lo que se puede antes de mandar y se traduce cada estado a un texto.

export const LINK_DEVICE_PATH = "/dispositivos/vincular";

export const LINK_STEPS = ["choice", "details", "compatibility"] as const;
export type LinkStep = (typeof LINK_STEPS)[number];

/** `claim`: lo vendió WAYAK (código de la caja). `own`: un GPS que ya tenía. */
export type LinkMode = "claim" | "own";

export type LinkStatus = "connected" | "waiting" | "no_data";

export const LINK_STATUSES: readonly LinkStatus[] = ["connected", "waiting", "no_data"];

/** Cada cuánto se vuelve a preguntar si el equipo ya reporta, y hasta cuándo. */
export const LINK_POLL_INTERVAL_MS = 15_000;
export const LINK_POLL_MAX_MS = 10 * 60_000;

export function nextStep(step: LinkStep): LinkStep {
  const i = LINK_STEPS.indexOf(step);
  return LINK_STEPS[Math.min(i < 0 ? 0 : i + 1, LINK_STEPS.length - 1)];
}

export function prevStep(step: LinkStep): LinkStep {
  const i = LINK_STEPS.indexOf(step);
  return LINK_STEPS[Math.max(i < 0 ? 0 : i - 1, 0)];
}

/** Código de caja / IMEI tal como lo guarda el backend: sin espacios ni guiones. */
export function normalizeIdentifier(value: string | null | undefined): string {
  return (value ?? "").replace(/[\s-]+/g, "").trim();
}

/** IMEI: exactamente 15 dígitos y dígito verificador de Luhn correcto (misma regla que el backend). */
export function isValidImei(value: string | null | undefined): boolean {
  const digits = normalizeIdentifier(value);
  if (!/^\d{15}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 15; i += 1) {
    let d = Number(digits[i]);
    if ((15 - i) % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

export interface OwnDeviceDraft {
  identifier: string;
  brandId: string;
  phoneNumber: string;
  simPassword: string;
  name: string;
}

export type OwnDeviceProblem = "imei" | "brand";

/** Qué falta o está mal en el formulario de GPS propio, en el orden en que se muestra. */
export function ownDeviceProblems(draft: OwnDeviceDraft): OwnDeviceProblem[] {
  const out: OwnDeviceProblem[] = [];
  if (!isValidImei(draft.identifier)) out.push("imei");
  if (!draft.brandId.trim()) out.push("brand");
  return out;
}

export function isLinkStatus(value: unknown): value is LinkStatus {
  return typeof value === "string" && (LINK_STATUSES as string[]).includes(value);
}

/** ¿Hay que seguir preguntando? Sólo mientras espera y no se agotó el plazo. */
export function shouldKeepPolling(status: LinkStatus | null, elapsedMs: number, maxMs: number = LINK_POLL_MAX_MS): boolean {
  if (status === "connected" || status === "no_data") return false;
  return elapsedMs < maxMs;
}

export type Lang = "es" | "en";

export interface StatusCopy {
  title: string;
  body: string;
  /** Tono visual (skill estados-y-tonos): un estado, un tono. */
  tone: "success" | "warning" | "error";
}

/** Texto de cada estado de la prueba de compatibilidad. */
export function statusCopy(status: LinkStatus | null, lang: Lang): StatusCopy {
  const en = lang === "en";
  switch (status) {
    case "connected":
      return {
        title: en ? "Connected: your device is reporting" : "Conectado: tu equipo ya reporta",
        body: en
          ? "We received its first position. You can see it on the map now."
          : "Recibimos su primera posición. Ya puedes verlo en el mapa.",
        tone: "success",
      };
    case "no_data":
      return {
        title: en ? "No data yet" : "Sin datos todavía",
        body: en
          ? "We haven't received a position in 10 minutes. Check that the device is powered, has signal and its SIM has data; or contact support and we'll configure it with you."
          : "No recibimos ninguna posición en 10 minutos. Revisa que el equipo tenga corriente, señal y datos en la SIM; o contacta a soporte y lo configuramos contigo.",
        tone: "error",
      };
    default:
      return {
        title: en ? "Waiting for the first position…" : "Esperando la primera posición…",
        body: en
          ? "Turn the device on with its SIM inserted. We check every 15 seconds for up to 10 minutes."
          : "Enciende el equipo con la SIM puesta. Revisamos cada 15 segundos, hasta 10 minutos.",
        tone: "warning",
      };
  }
}

/** Códigos estables (`meta.code`) o claves (`meta.message`) que manda la API al vincular. */
export type LinkErrorCode =
  | "DEVICE_NOT_CLAIMABLE"
  | "DEVICE_NOT_LINKABLE"
  | "DEVICE_RESERVED"
  | "DEVICE_ALREADY_LINKED"
  | "USE_CLAIM_INSTEAD"
  | "INVALID_IMEI"
  | "INVALID_SIM_PASSWORD"
  | "BRAND_NOT_FOUND"
  | "RATE_LIMITED"
  | "FORBIDDEN"
  | "UNKNOWN";

/** Códigos estables que manda la API (`meta.code`), tal cual. */
const STABLE_CODES: readonly LinkErrorCode[] = [
  "DEVICE_NOT_CLAIMABLE",
  "DEVICE_NOT_LINKABLE",
  "DEVICE_RESERVED",
  "DEVICE_ALREADY_LINKED",
  "USE_CLAIM_INSTEAD",
  "INVALID_IMEI",
  "INVALID_SIM_PASSWORD",
];

const MESSAGE_KEYS: Record<string, LinkErrorCode> = {
  deviceNotClaimable: "DEVICE_NOT_CLAIMABLE",
  deviceNotLinkable: "DEVICE_NOT_LINKABLE",
  deviceReserved: "DEVICE_RESERVED",
  deviceAlreadyLinked: "DEVICE_ALREADY_LINKED",
  useClaimInstead: "USE_CLAIM_INSTEAD",
  invalidImei: "INVALID_IMEI",
  invalidSimPassword: "INVALID_SIM_PASSWORD",
  brandNotFound: "BRAND_NOT_FOUND",
  deviceLinkForbidden: "FORBIDDEN",
  deviceLinkRateLimited: "RATE_LIMITED",
};

/**
 * Qué falló, a partir de la respuesta de la API (`{ meta: { code, message } }`).
 * El `code` es estable; `message` ya viene traducido por el backend, pero la
 * clave llega tal cual cuando no hay traducción, así que también se reconoce.
 */
export function linkErrorCode(err: unknown): LinkErrorCode {
  const e = err as { response?: { status?: number; data?: { meta?: { code?: unknown; message?: unknown } } } };
  const meta = e?.response?.data?.meta;
  const code = typeof meta?.code === "string" ? meta.code : "";
  if (code === "DEVICE_LINK_FORBIDDEN") return "FORBIDDEN";
  if (code === "DEVICE_LINK_RATE_LIMITED") return "RATE_LIMITED";
  if ((STABLE_CODES as string[]).includes(code)) return code as LinkErrorCode;
  const message = typeof meta?.message === "string" ? meta.message : "";
  if (MESSAGE_KEYS[message]) return MESSAGE_KEYS[message];
  if (e?.response?.status === 404 && /marca|brand/i.test(message)) return "BRAND_NOT_FOUND";
  if (e?.response?.status === 404) return "DEVICE_NOT_CLAIMABLE";
  // 409 sin código: la API no dice de quién es el IMEI; se ofrece soporte.
  if (e?.response?.status === 409) return "DEVICE_NOT_LINKABLE";
  if (e?.response?.status === 429) return "RATE_LIMITED";
  if (e?.response?.status === 403) return "FORBIDDEN";
  return "UNKNOWN";
}

/** Mensaje humano de la API, si lo hay y no es un código crudo («MustBeAString»). */
export function apiHumanMessage(err: unknown): string | null {
  const e = err as { response?: { data?: { meta?: { message?: unknown } } } };
  const raw = e?.response?.data?.meta?.message;
  if (typeof raw !== "string" || !raw.trim()) return null;
  return /^[\w.-]+$/.test(raw.trim()) ? null : raw;
}

export interface SupportDraftInput {
  mode: LinkMode;
  identifier: string;
  brandLabel?: string | null;
  phoneNumber?: string | null;
  status: LinkStatus | null;
  lang: Lang;
}

/**
 * Asunto y descripción prellenados del ticket «Contactar a soporte». Lleva
 * IMEI, marca/modelo y número de la SIM. NUNCA la contraseña de la SIM: vive
 * en `device_secrets` y la lee soporte por la API con su propio permiso.
 */
export function buildSupportDraft(input: SupportDraftInput): { title: string; description: string } {
  const en = input.lang === "en";
  const imei = normalizeIdentifier(input.identifier) || "—";
  const title = en ? `Device not reporting: IMEI ${imei}` : `Equipo sin reportar: IMEI ${imei}`;
  const rows = [
    `${en ? "Path" : "Camino"}: ${input.mode === "claim" ? (en ? "WAYAK device (box code)" : "equipo de WAYAK (código de caja)") : en ? "own GPS" : "GPS propio"}`,
    `IMEI: ${imei}`,
    `${en ? "Brand / model" : "Marca / modelo"}: ${input.brandLabel?.trim() || "—"}`,
    `${en ? "SIM number" : "Número de la SIM"}: ${input.phoneNumber?.trim() || "—"}`,
    `${en ? "Compatibility test" : "Prueba de compatibilidad"}: ${input.status ?? "—"}`,
  ];
  const lead = en
    ? "I linked the device from /dispositivos/vincular and it has not sent its first position."
    : "Vinculé el equipo desde /dispositivos/vincular y no ha mandado su primera posición.";
  return { title, description: `${lead}\n\n${rows.join("\n")}` };
}

/**
 * A dónde volver al terminar (`?back=/bienvenida`). Sólo rutas internas
 * relativas: nunca una URL externa, `//otro-sitio` ni una barra invertida
 * (los navegadores la tratan como barra normal: `/\otro-sitio`).
 */
export function readBackPath(search: string, fallback = "/map"): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const back = (params.get("back") || "").trim();
  if (!back || !back.startsWith("/") || back.startsWith("//") || /[\s<>\\]/.test(back)) return fallback;
  return back;
}
